import React, { useState, useEffect, useCallback } from "react";
import "../../common/index.scss";
import { connect } from "react-redux";
import {
  actOrderProductDetails,
} from "../../../actions";
import store from "../../../store";
import useSWR from "swr";
import { getMyOrderList } from "../../../services/index";
import classes from "./my_orders.module.scss";

const ChevronDown = () => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
    <path fillRule="evenodd" d="M5.23 7.21a.75.75 0 011.06.02L10 11.168l3.71-3.938a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z" clipRule="evenodd" />
  </svg>
);

const EyeIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
    <path d="M10 12.5a2.5 2.5 0 100-5 2.5 2.5 0 000 5z" />
    <path fillRule="evenodd" d="M.664 10.59a1.651 1.651 0 010-1.186A10.004 10.004 0 0110 3c4.257 0 7.893 2.66 9.336 6.41.147.381.146.804 0 1.186A10.004 10.004 0 0110 17c-4.257 0-7.893-2.66-9.336-6.41zM14 10a4 4 0 11-8 0 4 4 0 018 0z" clipRule="evenodd" />
  </svg>
);

const CloseIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
    <path d="M6.28 5.22a.75.75 0 00-1.06 1.06L8.94 10l-3.72 3.72a.75.75 0 101.06 1.06L10 11.06l3.72 3.72a.75.75 0 101.06-1.06L11.06 10l3.72-3.72a.75.75 0 00-1.06-1.06L10 8.94 6.28 5.22z" />
  </svg>
);

const EmptyOrdersIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
    <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 10.5V6a3.75 3.75 0 10-7.5 0v4.5m11.356-1.993l1.263 12c.07.665-.45 1.243-1.119 1.243H4.25a1.125 1.125 0 01-1.12-1.243l1.264-12A1.125 1.125 0 015.513 7.5h12.974c.576 0 1.059.435 1.119 1.007zM8.625 10.5a.375.375 0 11-.75 0 .375.375 0 01.75 0zm7.5 0a.375.375 0 11-.75 0 .375.375 0 01.75 0z" />
  </svg>
);

const getStatusClass = (status) => {
  if (!status) return classes.default;
  const s = status.toLowerCase();
  if (s.includes("deliver") || s.includes("complete") || s.includes("success")) return classes.completed;
  if (s.includes("pending") || s.includes("process")) return classes.pending;
  if (s.includes("cancel") || s.includes("fail") || s.includes("reject")) return classes.cancelled;
  return classes.default;
};

const inr = new Intl.NumberFormat("en-IN", { style: "decimal" });

const formatDate = (value) => {
  if (!value) return value;
  const datePart = value.toString().trim().split(" ")[0];
  const [year, month, day] = datePart.split("-");
  if (!year || !month || !day) return value;
  return `${day}-${month}-${year}`;
};

const formatAddress = (parts) => parts.filter(Boolean).join(", ");

const normalizeAddress = (value) =>
  (value || "").toString().trim().replace(/\s+/g, " ").toLowerCase();

const OrderCard = ({ item, expanded, onToggle, onViewProducts }) => {
  const billingAddress = formatAddress([
    item.address,
    item.city,
    item.state,
    item.country,
    item.pincode,
  ]);
  const rawShippingAddress = formatAddress([
    item.ship_address,
    item.ship_city,
    item.ship_state,
    item.ship_country || item.country,
    item.ship_pincode,
  ]);
  const shippingAddress = rawShippingAddress || billingAddress;
  const cartPrice =
    item.cart_price != null
      ? Number(item.cart_price)
      : Number(item.amount || 0) + Number(item.discount || 0);
  const showBillingAddress =
    !!billingAddress &&
    !!rawShippingAddress &&
    normalizeAddress(billingAddress) !== normalizeAddress(rawShippingAddress);

  return (
    <div className={classes.order_card}>
      <button
        type="button"
        className={classes.order_header}
        onClick={onToggle}
        aria-expanded={expanded}
      >
        <div className={classes.order_header_left}>
          <span className={classes.order_no}>#{item.order_no}</span>
          <span className={classes.order_date}>{formatDate(item.order_date)}</span>
        </div>
        <div className={classes.order_header_right}>
          <span className={`${classes.status_badge} ${getStatusClass(item.status)}`}>
            {item.status}
          </span>
          <span className={`${classes.chevron} ${expanded ? classes.open : ""}`}>
            <ChevronDown />
          </span>
        </div>
      </button>

      {expanded && (
        <div className={classes.order_body}>
          <div className={classes.detail_grid}>
            <div className={classes.detail_item}>
              <span className={classes.detail_label}>Order ID</span>
              <span className={classes.detail_value}>{item.order_id}</span>
            </div>
            {!!item.discount && (
              <div className={classes.detail_item}>
                <span className={classes.detail_label}>Cart Price</span>
                <span className={classes.detail_value}>{item.currency} {inr.format(cartPrice)}</span>
              </div>
            )}
            <div className={classes.detail_item}>
              <span className={classes.detail_label}>Amount Paid</span>
              <span className={classes.detail_value}>{item.currency} {inr.format(item.amount)}</span>
            </div>
            {!!item.promo_code && (
              <div className={classes.detail_item}>
                <span className={classes.detail_label}>Promo Code</span>
                <span className={classes.detail_value}>{item.promo_code}</span>
              </div>
            )}
            {!!item.discount && (
              <div className={classes.detail_item}>
                <span className={classes.detail_label}>Coupon Discount</span>
                <span className={classes.detail_value}>{item.currency} {inr.format(item.discount)}</span>
              </div>
            )}
            {!!item.giftcard_code && (
              <div className={classes.detail_item}>
                <span className={classes.detail_label}>Gift Card</span>
                <span className={classes.detail_value}>{item.giftcard_code}</span>
              </div>
            )}
            {!!item.redeemed_amount && (
              <div className={classes.detail_item}>
                <span className={classes.detail_label}>Redeemed Amount</span>
                <span className={classes.detail_value}>{item.currency} {inr.format(item.redeemed_amount)}</span>
              </div>
            )}
            <div className={classes.detail_item}>
              <span className={classes.detail_label}>Payment Mode</span>
              <span className={classes.detail_value}>{item.payment_mode}</span>
            </div>
            <div className={classes.detail_item}>
              <span className={classes.detail_label}>Payment Status</span>
              <span className={classes.detail_value}>{item.payment_status}</span>
            </div>
            <div className={classes.detail_item}>
              <span className={classes.detail_label}>Payment ID</span>
              <span className={classes.detail_value}>{item.payment_id}</span>
            </div>
            <div className={classes.detail_item}>
              <span className={classes.detail_label}>Shipping Address</span>
              <span className={classes.detail_value}>{shippingAddress}</span>
            </div>
            {showBillingAddress && (
              <div className={classes.detail_item}>
                <span className={classes.detail_label}>Billing Address</span>
                <span className={classes.detail_value}>{billingAddress}</span>
              </div>
            )}
          </div>

          <div className={classes.order_actions}>
            <button
              type="button"
              className={classes.view_products_btn}
              onClick={() => onViewProducts(item)}
            >
              <EyeIcon />
              View Products
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

const ProductModal = ({ product_details, order, onClose, symbol }) => {
  const handleOverlayClick = useCallback((e) => {
    if (e.target === e.currentTarget) onClose();
  }, [onClose]);

  useEffect(() => {
    const handleEsc = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", handleEsc);
    return () => document.removeEventListener("keydown", handleEsc);
  }, [onClose]);

  if (!product_details) return null;

  const items = product_details.items;
  const itemsTotal = items.reduce((sum, prod) => sum + prod.qty * prod.price, 0);
  const totalQty = items.reduce((sum, prod) => sum + prod.qty, 0);
  const totals = order || product_details;
  const amountPaid = totals.amount != null ? Number(totals.amount) : itemsTotal;
  const discount = totals.discount != null ? Number(totals.discount) : 0;
  const cartPrice =
    totals.cart_price != null ? Number(totals.cart_price) : amountPaid + discount;
  const hasDifference = Math.round(cartPrice) !== Math.round(amountPaid);

  return (
    <div className={classes.modal_overlay} onClick={handleOverlayClick}>
      <div className={classes.modal_dialog} role="dialog" aria-modal="true">
        <div className={classes.modal_header}>
          <div className={classes.modal_header_left}>
            <h4 className={classes.modal_title}>Product Details</h4>
            <span className={classes.modal_item_count}>
              {items.length} {items.length === 1 ? "item" : "items"}
            </span>
          </div>
          <button type="button" className={classes.modal_close} onClick={onClose} aria-label="Close">
            <CloseIcon />
          </button>
        </div>
        <div className={classes.modal_body}>
          {items.map((prod, i) => (
            <div className={classes.product_card} key={i}>
              <div className={classes.product_index}>{i + 1}</div>
              <div className={classes.product_info}>
                <p className={classes.product_name}>{prod.name}</p>
                <div className={classes.product_meta}>
                  <span className={classes.product_meta_item}>SKU: <span>{prod.sku}</span></span>
                  <span className={classes.product_meta_item}>Qty: <span>{prod.qty}</span></span>
                  <span className={classes.product_meta_item}>Price: <span>{symbol || ""} {inr.format(prod.price)}</span></span>
                </div>
              </div>
              <div className={classes.product_amount}>
                {symbol || ""} {inr.format(prod.qty * prod.price)}
              </div>
            </div>
          ))}
        </div>
        <div className={classes.modal_footer}>
          {hasDifference && (
            <>
              <div className={`${classes.modal_footer_row} ${classes.modal_footer_row_sub}`}>
                <span className={classes.modal_footer_label}>Cart Price ({totalQty} {totalQty === 1 ? "item" : "items"})</span>
                <span className={classes.modal_footer_subvalue}>{symbol || ""} {inr.format(cartPrice)}</span>
              </div>
              <div className={`${classes.modal_footer_row} ${classes.modal_footer_row_sub}`}>
                <span className={classes.modal_footer_label}>Discount</span>
                <span className={classes.modal_footer_discount}>- {symbol || ""} {inr.format(cartPrice - amountPaid)}</span>
              </div>
            </>
          )}
          <div className={`${classes.modal_footer_row} ${hasDifference ? classes.modal_footer_row_total : ""}`}>
            <span className={classes.modal_footer_label}>
              {hasDifference ? "Amount Paid" : `Total (${totalQty} ${totalQty === 1 ? "item" : "items"})`}
            </span>
            <span className={classes.modal_footer_value}>{symbol || ""} {inr.format(amountPaid)}</span>
          </div>
        </div>
      </div>
    </div>
  );
};

const orderListFetcher = () =>
  getMyOrderList().then((response) =>
    response.data.success === true ? response.data.order : null
  );

const MyOrderDetails = (props) => {
  const [modalOpen, setModalOpen] = useState(false);
  const [openOrderKey, setOpenOrderKey] = useState(null);
  const [selectedOrder, setSelectedOrder] = useState(null);

  const { data: order_details } = useSWR("my-orders", orderListFetcher, {
    revalidateOnFocus: false,
    revalidateOnReconnect: false,
    revalidateIfStale: false,
    dedupingInterval: Infinity,
  });

  useEffect(() => {
    if (!modalOpen) return undefined;
    const scrollY = window.scrollY;
    document.body.style.position = "fixed";
    document.body.style.top = `-${scrollY}px`;
    document.body.style.left = "0";
    document.body.style.right = "0";
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.position = "";
      document.body.style.top = "";
      document.body.style.left = "";
      document.body.style.right = "";
      document.body.style.overflow = "";
      window.scrollTo(0, scrollY);
    };
  }, [modalOpen]);

  const viewProduct = useCallback((order) => {
    store.dispatch({ type: "RECEIVE_ORDER_PRODUCTS", payload: "" });
    setSelectedOrder(order);
    props.actOrderProductDetails(order.order_id);
    setModalOpen(true);
  }, [props.actOrderProductDetails]);

  const { product_details } = props;

  if (!order_details) {
    return (
      <div className={classes.loader_section}>
        <img
          src={`${process.env.PUBLIC_URL}/assets/images/sunny/loader/loader.gif`}
          alt=""
          className={classes.loader}
        />
      </div>
    );
  }

  return (
    <div className={classes.orders_page}>
      <div className={classes.header}>
        <h1 className={classes.page_title}>My Orders</h1>
        <p className={classes.page_subtitle}>
          Track and manage all your orders in one place.
        </p>
      </div>

      {order_details.length > 0 ? (
        order_details.map((item, i) => {
          const orderKey = item.order_id || item.order_no || i;
          return (
            <OrderCard
              key={orderKey}
              item={item}
              expanded={openOrderKey === orderKey}
              onToggle={() =>
                setOpenOrderKey((prev) => (prev === orderKey ? null : orderKey))
              }
              onViewProducts={viewProduct}
            />
          );
        })
      ) : (
        <div className={classes.empty_state}>
          <div className={classes.empty_icon}>
            <EmptyOrdersIcon />
          </div>
          <h3 className={classes.empty_title}>No Orders Found</h3>
          <p className={classes.empty_text}>Looks like you haven't placed any orders yet.</p>
        </div>
      )}

      {modalOpen && product_details && (
        <ProductModal
          product_details={product_details}
          order={selectedOrder}
          onClose={() => setModalOpen(false)}
          symbol={props.symbol}
        />
      )}
    </div>
  );
};

const mapStateToProps = (state) => ({
  product_details: state.data.order_products,
  symbol: state.data.symbol,
});

export default connect(mapStateToProps, {
  actOrderProductDetails,
})(MyOrderDetails);
